import React from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Input: React.FC<InputProps> = ({
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  id,
  className = '',
  ...props
}) => {
  const inputId = id || `input-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const hasError = !!error;

  return (
    <div className={`${className}`}>
      {/* Label */}
      <label
        htmlFor={inputId}
        className={`block text-sm font-medium mb-2 ${
          hasError ? 'text-error-500' : 'text-monday-text-muted'
        }`}
      >
        {label}
      </label>

      {/* Input Container */}
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-monday-text-muted pointer-events-none">
            {leftIcon}
          </div>
        )}

        <input
          id={inputId}
          className={`
            w-full px-3.5 py-[10px] min-h-[42px]
            ${leftIcon ? 'pl-10' : ''}
            ${rightIcon ? 'pr-10' : ''}
            text-sm text-monday-text
            bg-white/95 border rounded-xl shadow-xs
            transition-all duration-200
            focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)]
            disabled:bg-monday-header disabled:text-monday-text-muted disabled:cursor-not-allowed
            placeholder:text-monday-text-muted/60
            ${hasError
              ? 'border-error-500 focus:border-error-500 focus:shadow-[0_0_0_4px_rgba(199,51,64,0.16)]'
              : 'border-monday-border focus:border-primary-500 hover:border-neutral-300'
            }
          `}
          {...props}
        />

        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-monday-text-muted pointer-events-none">
            {rightIcon}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="mt-1 text-xs text-error-500 flex items-center gap-1">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}

      {/* Hint */}
      {hint && !error && (
        <p className="mt-1 text-xs text-monday-text-muted">{hint}</p>
      )}
    </div>
  );
};

export default Input;
